/**
 * Module-scoped user context store and metadata generator.
 * Runs outside the React tree so interceptors can access it.
 */

interface UserContext {
  user_id: string
  organization_id: string
  email: string
  groups: string
}

let currentUser: UserContext | null = null

export function setUserContext(ctx: UserContext | null): void {
  currentUser = ctx
}

export function getUserContext(): UserContext | null {
  return currentUser
}

function getPlatform(): string {
  const ua = navigator.userAgent.toLowerCase()

  // Check mobile platforms first (before mac/linux checks)
  if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod")) {
    return "ios"
  }
  if (ua.includes("android")) {
    return "android"
  }

  // Desktop platforms
  if (ua.includes("mac")) return "mac"
  if (ua.includes("win")) return "windows"
  if (ua.includes("linux")) return "linux"

  return "browser"
}

function safeGetTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return "unknown"
  }
}

export function buildEventMetadata(): Record<string, unknown> {
  return {
    user_id: currentUser?.user_id ?? null,
    organization_id: currentUser?.organization_id ?? null,
    source: "web",
    version:
      typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown",
    platform: getPlatform(),
    environment: import.meta.env.VITE_ENVIRONMENT ?? "unknown",
    // Client context fields with safe fallbacks
    user_agent: navigator.userAgent?.substring(0, 200) ?? "unknown",
    locale: navigator.language ?? "unknown",
    timezone: safeGetTimezone(),
    client_timestamp: new Date().toISOString(),
  }
}

export function serializeMetadataHeader(): string {
  try {
    return JSON.stringify(buildEventMetadata())
  } catch {
    return "{}"
  }
}
